import { Koi } from '@ksyos/koi';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { emailSchema, onePhoneNumberRequiredSchema, phoneNumberSchema } from '@common/validation/schemas';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type { ProcessActivityAnamnesisData } from '../../../processActivityAnamnesisData';
import type { ProcessActivityMedicationData } from '../../../processActivityMedicationData';
import type { ProcessActivityProfessionalSummaryData } from '../../../processActivityProfessionalSummaryData';
import {
    processActivityAnamnesisDataSchema,
    processActivityAnamnesisDraftDataSchema,
} from '../../../processActivityAnamnesisData';
import {
    processActivityMedicationDataSchema,
    processActivityMedicationDraftDataSchema,
} from '../../../processActivityMedicationData';
import { processActivityProfessionalSummaryDataSchema } from '../../../processActivityProfessionalSummaryData';

export type RespiAllerReferralCustomControlData = {
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
    isPatientInformed: boolean;
    patient: { emailAddress: string | null; homePhoneNumber: string | null; mobileNumber: string | null };
    referralQuestion: string | null;
};

export type RespiAllerReferralData = RespiAllerReferralCustomControlData &
    ProcessActivityAnamnesisData &
    ProcessActivityProfessionalSummaryData &
    ProcessActivityMedicationData;

type TypedRespiAllerReferralSchema = TypedSchema<RespiAllerReferralCustomControlData>;

const respiAllerReferralFinalBaseSchema: TypedRespiAllerReferralSchema = {
    draftCareProviderOrganizationId: Koi.string().uuid().allow(null).optional(),
    draftCareProviderPractitionerId: Koi.string().uuid().allow(null).optional(),
    isPatientInformed: Koi.boolean().optional(),
    patient: Koi.object({
        emailAddress: emailSchema,
    }).concat(onePhoneNumberRequiredSchema('homePhoneNumber', 'mobileNumber')),
    referralQuestion: getTextAreaStringSchema(true, 10_000),
};

const respiAllerReferralDraftBaseSchema: TypedRespiAllerReferralSchema = {
    draftCareProviderOrganizationId: Koi.string().allow(null).optional(),
    draftCareProviderPractitionerId: Koi.string().allow(null).optional(),
    isPatientInformed: Koi.boolean().optional(),
    patient: Koi.object({
        emailAddress: emailSchema.allow(null),
        homePhoneNumber: phoneNumberSchema.allow(null),
        mobileNumber: phoneNumberSchema.allow(null),
    }),
    referralQuestion: getTextAreaStringSchema(true, 10_000),
};

const respiAllerReferralCreatePatientResponse = {
    url: Koi.string().uri().max(1_000).required(),
};

export const respiAllerReferralFinalSchema = Koi.object(respiAllerReferralFinalBaseSchema)
    .concat(processActivityAnamnesisDataSchema)
    .concat(processActivityProfessionalSummaryDataSchema)
    .concat(processActivityMedicationDataSchema);
export const respiAllerReferralDraftSchema = Koi.object(respiAllerReferralDraftBaseSchema)
    .concat(processActivityAnamnesisDraftDataSchema)
    .concat(processActivityProfessionalSummaryDataSchema)
    .concat(processActivityMedicationDraftDataSchema);
export const respiAllerReferralCreatePatientResponseSchema = Koi.object(respiAllerReferralCreatePatientResponse);
